import clsx from 'clsx'
import { ArrowUpDown, Eye } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CampaignDetailsModal from '@/components-latest/ui/CampaignsPage/CampaignDetailsModal'
import CampaignsFilters from '@/components-latest/ui/CampaignsPage/CampaignsFilters'
import CampaignsNoResults from '@/components-latest/ui/CampaignsPage/CampaignsNoResults'
import type { CampaignFilterHandlers } from '@/components-latest/ui/CampaignsPage/useCampaignsFilters'
import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'
import {
  getCampaignExpirationText,
  isReadOnlyCampaign
} from '@/ducks/campaigns/campaigns.helpers'
import type {
  Campaign,
  CampaignFilters,
  CampaignLimit
} from '@/ducks/campaigns/campaigns.types'

import CampaignActionsMenu from './CampaignActionsMenu'
import CampaignOfferCell from './CampaignOfferCell'
import Styles from './CampaignsTable.module.scss'
import CampaignsTableFooter from './CampaignsTableFooter'
import CampaignStatusCell from './CampaignStatusCell'

type Props = {
  campaigns: Campaign[]
  page: number
  limit: CampaignLimit
  total: number
  filters: CampaignFilters
  hasActiveFilters: boolean
  campaignsQueryParams?: FetchCampaignsQueryParams
  handlers: CampaignFilterHandlers
  onPageChange: (page: number) => void
  onLimitChange: (limit: CampaignLimit) => void
}

const CampaignsTable = ({
  campaigns,
  page,
  limit,
  total,
  filters,
  hasActiveFilters,
  campaignsQueryParams,
  handlers,
  onPageChange,
  onLimitChange
}: Props) => {
  const { t } = useTranslation()
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  )
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign)
    setIsDetailsOpen(true)
  }

  const handleDetailsOpenChange = (open: boolean) => {
    setIsDetailsOpen(open)
    if (!open) {
      setSelectedCampaign(null)
    }
  }

  const showNoResults = hasActiveFilters && campaigns.length === 0

  return (
    <div className={Styles.wrapper} data-testid="campaigns-table">
      <div className={Styles.filtersRow}>
        <CampaignsFilters filters={filters} handlers={handlers} />
      </div>

      {showNoResults ? (
        <CampaignsNoResults />
      ) : (
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <colgroup>
              <col className={Styles.offerCol} />
              <col className={Styles.statusCol} />
              <col className={Styles.expirationCol} />
              <col className={Styles.actionsCol} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <span>{t('marketing.campaigns.table.offer')}</span>
                  <ArrowUpDown width={16} height={16} />
                </th>
                <th>
                  <span>{t('marketing.campaigns.table.status')}</span>
                  <ArrowUpDown width={16} height={16} />
                </th>
                <th>
                  <span>{t('marketing.campaigns.table.expiration')}</span>
                  <ArrowUpDown width={16} height={16} />
                </th>
                <th className={Styles.actionsHeader}>
                  <span>{t('marketing.campaigns.table.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(campaign => {
                const isYassirCampaign = isReadOnlyCampaign(campaign)

                return (
                  <tr key={campaign.campaignId}>
                    <td className={Styles.offerCell}>
                      <CampaignOfferCell campaign={campaign} />
                    </td>
                    <td className={Styles.statusCell}>
                      <CampaignStatusCell campaign={campaign} />
                    </td>
                    <td className={Styles.expirationCell}>
                      {getCampaignExpirationText(campaign, t)}
                    </td>
                    <td className={Styles.actionsCellColumn}>
                      <div
                        className={clsx(
                          Styles.actionsCell,
                          isYassirCampaign && Styles.actionsCellSingle
                        )}>
                        <div className={Styles.actionSlot}>
                          <button
                            type="button"
                            className={Styles.iconButton}
                            aria-label={t('marketing.campaigns.view-details')}
                            onClick={() => handleViewDetails(campaign)}>
                            <Eye width={16} height={16} />
                          </button>
                        </div>
                        {!isYassirCampaign && (
                          <div className={Styles.actionSlot}>
                            <CampaignActionsMenu
                              campaign={campaign}
                              campaignsQueryParams={campaignsQueryParams}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <CampaignsTableFooter
        page={page}
        limit={limit}
        total={total}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />

      <CampaignDetailsModal
        campaign={selectedCampaign}
        open={isDetailsOpen}
        onOpenChange={handleDetailsOpenChange}
        campaignsQueryParams={campaignsQueryParams}
      />
    </div>
  )
}

export default CampaignsTable
